import React, { useEffect, useState, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import isEmpty from 'lodash/isEmpty';
import SelectMultipleField from '../../../../../FormControl/input/SelectMultipleField';
import DialogConfirmCancel from '../../../../../dialog/DialogConfirm';
import InputField from '../../../../../FormControl/input/InputField';
import { useDispatch, useSelector } from 'react-redux';
import { updateFilter, showFilter } from '../../../../../../store/actions/filter.actions';
import { updateSkip } from '../../../../../../store/actions/datatable.actions';
import { SelectFieldGender } from '../../../../../FormControl/SelectCustom/SelectFieldGender';
import { SelectMultipleFieldFlags } from '../../../../../FormControl/SelectRequest/SelectMultipleFieldFlags';
import { useLocation, useNavigate } from 'react-router-dom';
import SelectField from '../../../../../FormControl/input/SelectField';
import {
  SURVEY_TYPE_ABOUT_USERS,
  SURVEY_TYPE_STANDARD,
  SURVEY_TYPE_ABOUT_CLASSES
} from '../../../constants/typeSurvey';

const FilterDialog = ({ dataYears = [], errorFetchYears, isLoadingYears, type = SURVEY_TYPE_STANDARD }) => {
  const date = useSelector((state) => state?.date?.date || new Date());
  const filter = useSelector((state) => state?.filter.surveyPreviewList?.filter || {});
  const open = useSelector((state) => state?.filter.surveyPreviewList?.open || false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const statusOptions = useMemo(() => {
    let op = [{ value: 'pending', label: 'Pending' }];

    if (type === SURVEY_TYPE_STANDARD) {
      op = [
        { value: 'pending', label: 'Pending' },
        { value: 'completed', label: 'Completed' }
      ];
    }
    return op;
  }, [type]);

  const yearsData = dataYears.map((item) => ({
    _id: item._id,
    name: item.name,
    code: item.code
  }));

  const dispatch = useDispatch();

  const [form, setInputForm] = useState(filter);
  const [error, setInputError] = useState({});

  useEffect(() => {
    if (!isEmpty(filter)) {
      setInputForm(filter);
    }
  }, [filter]);

  const onClose = () => {
    dispatch(showFilter('surveyPreviewList', false));
  };

  const onChange = (name, value) => {
    if (name === 'yearLevelId') {
      setInputError({ ...error, [name]: null, yearLevel: null });
      const yearLevel = yearsData.filter((item) => value.includes(item._id));
      setInputForm({ ...form, [name]: value, yearLevel });
    } else {
      setInputForm({ ...form, [name]: value });
      setInputError({ ...error, [name]: null });
    }
  };

  const onApply = () => {
    onClose();
    const filter = {
      ...form
    };

    dispatch(updateFilter('surveyPreviewList', filter));
    dispatch(updateSkip('surveyPreviewList', 0));
    navigate(pathname, { state: filter });
  };

  return (
    <DialogConfirmCancel
      id='builder-user-filter'
      open={open}
      title='Filter'
      maxWidth='xs'
      onCancel={onClose}
      onConfirm={onApply}
      textConfirm='Apply'
    >
      <Box className='flex flex-col gap-4'>
        <div className='grid grid-cols-3 items-center gap-4'>
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            {type === SURVEY_TYPE_ABOUT_CLASSES ? 'Class ID' : 'Code'}
          </Typography>
          <div className='col-span-2'>
            <InputField
              id='form-filter-code'
              name='code'
              value={form.code}
              error={error.code}
              helperText={error.code}
              maxDate={date}
              onChange={(e) => onChange('code', e.target.value)}
            />
          </div>

          {[...SURVEY_TYPE_ABOUT_USERS, SURVEY_TYPE_STANDARD].includes(type) && (
            <>
              <Typography
                variant='inherit'
                className='font-bold capitalize'
                color='primary'
                align='right'
                component='p'
              >
                Name
              </Typography>
              <div className='col-span-2'>
                <InputField
                  id='form-filter-first-name'
                  name='name'
                  value={form.name}
                  error={error.name}
                  helperText={error.name}
                  maxDate={date}
                  onChange={(e) => onChange('name', e.target.value)}
                />
              </div>
            </>
          )}

          {type === SURVEY_TYPE_STANDARD && (
            <>
              <Typography
                variant='inherit'
                className='font-bold capitalize'
                color='primary'
                align='right'
                component='p'
              >
                Status
              </Typography>
              <div className='col-span-2'>
                <SelectField
                  options={statusOptions}
                  id='survey-status'
                  name='status'
                  placeholder='Status'
                  value={form?.status || ''}
                  onChange={(e) => onChange('status', e.target.value)}
                  variant='standard'
                  onClear={() => onChange('status', '')}
                />
              </div>
            </>
          )}

          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            year level
          </Typography>
          <div className='col-span-2'>
            <SelectMultipleField
              id='form-filter-year-level'
              name='yearLevelId'
              value={form.yearLevelId}
              error={error.yearLevelId || errorFetchYears}
              helperText={error.yearLevelId || errorFetchYears}
              onChange={(e) => onChange('yearLevelId', e.target.value)}
              loading={isLoadingYears}
              options={yearsData?.map((item) => ({ value: item._id, label: item.name })) || []}
              placeholder='Choose a year level'
            />
          </div>

          {type === SURVEY_TYPE_STANDARD && (
            <>
              <Typography
                variant='inherit'
                className='font-bold capitalize'
                color='primary'
                align='right'
                component='p'
              >
                house
              </Typography>
              <div className='col-span-2'>
                <InputField
                  id='form-filter-house'
                  name='house'
                  value={form.house}
                  error={error.house}
                  helperText={error.house}
                  onChange={(e) => onChange('house', e.target.value)}
                />
              </div>
              <Typography
                variant='inherit'
                className='font-bold capitalize'
                color='primary'
                align='right'
                component='p'
              >
                form
              </Typography>
              <div className='col-span-2'>
                <InputField
                  id='form-filter-form'
                  name='form'
                  value={form.form}
                  error={error.form}
                  helperText={error.form}
                  onChange={(e) => onChange('form', e.target.value)}
                />
              </div>
              <Typography
                variant='inherit'
                className='font-bold capitalize'
                color='primary'
                align='right'
                component='p'
              >
                flag
              </Typography>
              <div className='col-span-2'>
                <SelectMultipleFieldFlags
                  id='form-filter-flag'
                  name='flag'
                  value={form.flag}
                  error={error.flag}
                  helperText={error.flag}
                  onChange={(e) => onChange('flag', e.target.value)}
                  fieldValue='importName'
                />
              </div>
            </>
          )}
        </div>
      </Box>
    </DialogConfirmCancel>
  );
};

export default FilterDialog;
